import { supabase } from '../supabaseClient';
import { deriveKey, encryptFile, decryptFile } from '../utils/patientFileCrypto';
import { uploadWithProgress } from '../utils/storageUpload';

const EVIDENCE_BUCKET = 'scribe-consent-documents';
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const mimeFor = name => /\.pdf$/i.test(name) ? 'application/pdf' : /\.png$/i.test(name) ? 'image/png' : /\.jpe?g$/i.test(name) ? 'image/jpeg' : null;

export function validateConsentEvidence(selection) {
  const file = selection?.file || selection?.existing;
  if (!file || !mimeFor(file.name) || !file.size || file.size > MAX_FILE_BYTES) {
    throw new Error('Choose a PDF, JPG or PNG document up to 10 MB.');
  }
}

export async function getClientConsents(patientId, offset = 0) {
  const { data, error } = await supabase.rpc('get_scribe_client_consent_log', { p_patient_id: patientId, p_offset: offset, p_limit: 25 });
  if (error || !data || !Array.isArray(data.events) || !Number.isSafeInteger(data.total)) throw new Error('Unable to load consent records. Please retry.');
  return data;
}

export async function recordClientConsent({ patientId, eventId, kind, occurredAt, channel, notes, consentId, document }) {
  const { data, error } = await supabase.rpc('record_scribe_client_consent', {
    p_patient_id: patientId, p_event_id: eventId, p_event_kind: kind, p_occurred_at: occurredAt,
    p_channel: channel, p_notes: notes || null, p_consent_id: consentId || null, p_document: document || null,
  });
  if (error || !data) throw new Error('Unable to save consent. Please retry.');
  return data;
}

export async function listConsentSourceFiles(patientId) {
  const { data, error } = await supabase.rpc('get_scribe_consent_source_files', { p_patient_id: patientId });
  if (error || !Array.isArray(data)) throw new Error('Unable to load your client files. Please retry.');
  return data;
}

export async function getConsentDocuments(patientId) {
  const { data, error } = await supabase.rpc('get_scribe_consent_documents', { p_patient_id: patientId });
  if (error || !Array.isArray(data)) throw new Error('Unable to load consent documents. Please retry.');
  return data;
}

async function consentKey(patientId) {
  if (!process.env.REACT_APP_ENCRYPTION_SECRET) throw new Error('Document encryption is unavailable. Please contact your administrator.');
  return deriveKey(patientId);
}

export async function uploadConsentEvidence(patientId, eventId, selection) {
  validateConsentEvidence(selection);
  const key = await consentKey(patientId);
  let file = selection?.file;
  if (selection?.existing) {
    const source = selection.existing;
    if (!source.path?.startsWith(`${patientId}/`) || !mimeFor(source.name)) throw new Error('Invalid client file.');
    const { data, error } = await supabase.storage.from('tikcare-attachments').download(source.path);
    if (error || !data) throw new Error('Unable to read the selected client file. Please retry.');
    const plain = await decryptFile(data, key, source.path.split('/').pop(), { allowLegacy: false });
    file = new File([plain], source.name, { type: mimeFor(source.name) });
  }
  const type = file && mimeFor(file.name);
  if (!file || !type || !file.size || file.size > MAX_FILE_BYTES) throw new Error('Choose a PDF, JPG or PNG document up to 10 MB.');
  const { data: session } = await supabase.auth.getSession();
  const userId = session?.session?.user?.id;
  if (!userId) throw new Error('Please sign in before uploading a consent document.');
  const name = `${eventId}.enc`;
  const path = `${patientId}/${userId}/${name}`;
  const cipher = await encryptFile(file, key, name);
  try {
    // Evidence is immutable: retry at the form level without enabling overwrite.
    await uploadWithProgress(EVIDENCE_BUCKET, path, cipher, { upsert: false, maxAttempts: 1, contentType: 'application/octet-stream' });
  } catch (error) {
    // A successful upload with a lost response is safe to retry only if its bytes match.
    const existing = await supabase.storage.from(EVIDENCE_BUCKET).download(path);
    if (existing.error || !existing.data) throw new Error('Unable to confirm consent document upload. Please retry.');
    const [a, b] = await Promise.all([cipher.arrayBuffer(), existing.data.arrayBuffer()]);
    const left = new Uint8Array(a), right = new Uint8Array(b);
    if (left.length !== right.length || left.some((value, index) => value !== right[index])) throw new Error('Consent upload changed. Cancel, refresh and select the document again.');
  }
  return { path, name: file.name, type, size: file.size };
}

export async function downloadConsentEvidence(patientId, document) {
  if (!document?.path?.startsWith(`${patientId}/`) || !mimeFor(document.name)) throw new Error('Invalid consent document.');
  const { data, error } = await supabase.storage.from(EVIDENCE_BUCKET).download(document.path);
  if (error || !data) throw new Error('Unable to download consent document. Please retry.');
  const key = await consentKey(patientId);
  const plain = await decryptFile(data, key, document.path.split('/').pop(), { allowLegacy: false });
  return { blob: new Blob([plain], { type: document.type || mimeFor(document.name) }), name: document.name };
}

export async function loadClientConsentLog(email, offset = 0) {
  const normalized = (email || '').trim().toLowerCase();
  if (!normalized) throw new Error('Add an email to this client’s profile to look up MindVault sharing records.');
  const { data, error } = await supabase.rpc('get_scribe_profile_consent_log', {
    p_client_email: normalized, p_offset: offset, p_limit: 25,
  });
  if (error) throw new Error('Unable to load MindVault sharing records. Please retry.');
  if (!data || !Array.isArray(data.events) || typeof data.total !== 'number' || typeof data.sharing !== 'boolean') {
    throw new Error('MindVault sharing records are unavailable. Please retry.');
  }
  return data;
}
